/**
 * leadAssignment — Lead-Hunter-Ergebnisse Agenten zuweisen (rein, ohne DB).
 *
 * Konfliktregel (keine Dubletten): Ein gefundener Lead, der schon als Kontakt
 * existiert, wird NICHT erneut angelegt/zugewiesen. Hat er eine offene Aufgabe,
 * wird der zuständige Agent samt Aufgabe genannt.
 *
 * Verteilregeln:
 *   fest           alle an einen Agenten
 *   gleichmaessig  jeweils an den Agenten mit der geringsten Auslastung
 *                  (offene Aufgaben + in diesem Lauf bereits zugewiesen)
 *   gebiet         Agent mit Kontakten in derselben PLZ (×3) / PLZ-Region 2-stellig (×1)
 *                  (+0,5 Branche nur bei Gebietstreffer); ohne Gebietstreffer → geringste Auslastung
 * Optional: maxPerAgent = Obergrenze neuer Leads je Agent in diesem Lauf.
 */
import { findDuplicateLead } from '@/components/utils/LeadCategorization';

const region = (plz) => String(plz || '').slice(0, 2);
const lc = (s) => String(s || '').trim().toLowerCase();

/** @returns {null | { reason: 'task'|'lead', agent: string|null, leadId: string, leadName: string, taskTypes: string[] }} */
export function findConflict(lead, existingLeads, openTasks) {
  const dup = findDuplicateLead(lead, existingLeads);
  if (!dup) return null;
  const tasks = (openTasks || []).filter((t) => t.lead_id === dup.id);
  return {
    reason: tasks.length ? 'task' : 'lead',
    agent: tasks[0]?.assigned_agent || dup.assigned_agent || null,
    leadId: dup.id,
    leadName: dup.name,
    taskTypes: [...new Set(tasks.map((t) => t.task_type || t.typ).filter(Boolean))],
  };
}

/** Dubletten innerhalb eines Imports: nur der erste Treffer bleibt */
export function dedupeBatch(batch) {
  const seen = [];
  return batch.map((lead) => {
    const dup = findDuplicateLead(lead, seen);
    if (!dup) seen.push(lead);
    return { lead, kept: !dup };
  });
}

export function buildAgentStats(agents, existingLeads, openTasks) {
  const stats = new Map(agents.map((a) => [lc(a.email), { email: a.email, openTasks: 0, plz: new Map(), region: new Map(), branche: new Map() }]));
  const nameToEmail = new Map(agents.filter((a) => a.name).map((a) => [lc(a.name), lc(a.email)]));
  const keyOf = (v) => (stats.has(lc(v)) ? lc(v) : nameToEmail.get(lc(v)));
  (openTasks || []).forEach((t) => {
    const k = keyOf(t.assigned_agent);
    if (k) stats.get(k).openTasks += 1;
  });
  (existingLeads || []).forEach((l) => {
    const k = keyOf(l.assigned_agent);
    if (!k) return;
    const s = stats.get(k);
    const inc = (m, key) => key && m.set(key, (m.get(key) || 0) + 1);
    inc(s.plz, String(l.plz || ''));
    inc(s.region, region(l.plz));
    inc(s.branche, lc(l.branche));
  });
  return stats;
}

/** Gebietstreffer nötig; Branche entscheidet nur zwischen Agenten im selben Gebiet */
function areaScore(s, lead) {
  const area = (s.plz.get(String(lead.plz || '')) || 0) * 3 + (s.region.get(region(lead.plz)) || 0);
  if (area === 0) return 0;
  return area + ((s.branche.get(lc(lead.branche)) || 0) > 0 ? 0.5 : 0);
}

/**
 * @param {object[]} leads
 * @param {{ rule: 'fest'|'gleichmaessig'|'gebiet', agentEmails: string[], stats: Map, fixedAgent?: string, maxPerAgent?: number }} opts
 * @returns {(string|null)[]} Agent-E-Mail je Lead (null = keiner verfügbar)
 */
export function planAssignments(leads, { rule, agentEmails, stats, fixedAgent = null, maxPerAgent = null }) {
  if (rule === 'fest') return leads.map(() => fixedAgent || null);
  const pool = (agentEmails || []).filter((e) => stats.has(lc(e)));
  const added = new Map(pool.map((e) => [lc(e), 0]));
  const load = (e) => stats.get(lc(e)).openTasks + added.get(lc(e));
  const available = () => pool.filter((e) => !maxPerAgent || added.get(lc(e)) < maxPerAgent);

  return leads.map((lead) => {
    const candidates = available();
    if (candidates.length === 0) return null;
    let best = null;
    if (rule === 'gebiet') {
      const scored = candidates.map((e) => ({ e, s: areaScore(stats.get(lc(e)), lead) })).filter((x) => x.s > 0);
      if (scored.length) {
        scored.sort((a, b) => b.s - a.s || load(a.e) - load(b.e));
        best = scored[0].e;
      }
    }
    if (!best) best = [...candidates].sort((a, b) => load(a) - load(b))[0];
    added.set(lc(best), added.get(lc(best)) + 1);
    return best;
  });
}
